import { useRef, useState } from 'react'
import { BookOpenText, FileAudio, FileText, LoaderCircle, RotateCw, Trash2, UploadCloud } from 'lucide-react'
import { mergeTranscripts, splitAudioForTranscription } from '../services/audioService'
import { transcribeAudio } from '../services/apiService'
import {
  createTranscriptionJob,
  getFileBlob,
  softDeleteFile,
  updateTranscriptionJob,
  uploadLessonFile,
} from '../services/lessonService'
import { extractPdfText } from '../services/pdfService'
import type { LessonFile } from '../types'

type Props = {
  lessonId: string
  userId: string
  files: LessonFile[]
  language: string
  model: 'gpt-4o-mini-transcribe' | 'gpt-4o-transcribe'
  onFilesChanged: () => Promise<void>
  onTranscript: (text: string) => Promise<void>
  onPdfText: (text: string, name: string, pages: string[]) => void
}

const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'mp4', 'wav', 'webm', 'ogg', 'mpeg', 'mpga', 'flac']
const MAX_UPLOAD_SIZE = 500 * 1024 * 1024

const isAudio = (file: LessonFile) =>
  file.file_type.startsWith('audio/') || AUDIO_EXTENSIONS.includes(file.file_name.split('.').pop()?.toLowerCase() || '')

const formatBytes = (bytes: number) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} МБ` : `${Math.ceil(bytes / 1024)} КБ`

export const FileManager = ({
  lessonId,
  userId,
  files,
  language,
  model,
  onFilesChanged,
  onTranscript,
  onPdfText,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busyFile, setBusyFile] = useState<string | null>(null)
  const [progress, setProgress] = useState('')
  const [error, setError] = useState('')

  const upload = async (selected: FileList | null) => {
    if (!selected?.length) return
    setError('')
    for (const file of Array.from(selected)) {
      const extension = file.name.split('.').pop()?.toLowerCase() || ''
      if (!AUDIO_EXTENSIONS.includes(extension) && extension !== 'pdf') {
        setError(`${file.name}: бұл форматқа рұқсат жоқ.`)
        continue
      }
      if (!file.size || file.size > MAX_UPLOAD_SIZE) {
        setError(`${file.name}: файл бос немесе 500 МБ-тан үлкен.`)
        continue
      }
      setBusyFile(file.name)
      setProgress(`${file.name} бұлтқа жүктелуде...`)
      try {
        await uploadLessonFile(lessonId, userId, file, file.name)
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : 'Файл жүктеу қатесі')
      }
    }
    setBusyFile(null)
    setProgress('')
    if (inputRef.current) inputRef.current.value = ''
    await onFilesChanged()
  }

  const transcribe = async (file: LessonFile) => {
    setBusyFile(file.id)
    setError('')
    const job = await createTranscriptionJob(lessonId, file.id, userId, language)
    try {
      setProgress('Аудио дайындалуда...')
      await updateTranscriptionJob(job.id, { status: 'uploading', progress: 5 })
      const blob = await getFileBlob(file)
      const parts = await splitAudioForTranscription(blob)
      const results: string[] = []
      for (let index = 0; index < parts.length; index += 1) {
        const label = `${index + 1}/${parts.length} бөлік мәтінге айналдырылуда`
        setProgress(label)
        await updateTranscriptionJob(job.id, {
          status: 'transcribing',
          progress: Math.round(((index + 0.2) / parts.length) * 100),
        })
        const extension = parts[index].type.includes('wav') ? 'wav' : file.file_name.split('.').pop() || 'webm'
        const response = await transcribeAudio(
          parts[index],
          `part-${index + 1}.${extension}`,
          language,
          lessonId,
          model,
          results.at(-1)?.slice(-500) || '',
        )
        results.push(response.text)
      }
      await onTranscript(mergeTranscripts(results))
      await updateTranscriptionJob(job.id, { status: 'completed', progress: 100 })
      setProgress('Мәтін дайын және автоматты сақталды.')
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Транскрипция қатесі'
      setError(message)
      setProgress('')
      await updateTranscriptionJob(job.id, { status: 'failed', error_message: message })
    } finally {
      setBusyFile(null)
    }
  }

  const readPdf = async (file: LessonFile) => {
    setBusyFile(file.id)
    setError('')
    try {
      const blob = await getFileBlob(file)
      const result = await extractPdfText(blob, (current, total) => setProgress(`${current}/${total} PDF беті оқылуда`))
      if (!result.text.trim()) throw new Error('PDF ішінде мәтін табылмады. Бұл скан болуы мүмкін; OCR келесі модульге дайындалған.')
      onPdfText(result.text, file.file_name, result.pages)
      setProgress('PDF мәтіні дайын.')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'PDF оқу қатесі')
      setProgress('')
    } finally {
      setBusyFile(null)
    }
  }

  const openFile = async (file: LessonFile) => {
    const blob = await getFileBlob(file)
    window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer')
  }

  const remove = async (file: LessonFile) => {
    if (!window.confirm(`«${file.file_name}» файлын себетке жібереміз бе? Бастапқы Storage файлы бірден өшірілмейді.`)) return
    await softDeleteFile(file)
    await onFilesChanged()
  }

  return (
    <section className="panel">
      <div className="panel-heading">
        <div><p className="eyebrow">АУДИО ЖӘНЕ PDF</p><h3>Сабақ материалдары</h3></div>
        <button className="primary" onClick={() => inputRef.current?.click()}><UploadCloud size={17} /> Файл жүктеу</button>
        <input ref={inputRef} hidden type="file" multiple accept="audio/*,.mp3,.m4a,.mp4,.wav,.webm,.ogg,.mpeg,.mpga,.flac,.pdf" onChange={(event) => upload(event.target.files)} />
      </div>
      {progress && <div className="notice info">{busyFile && <LoaderCircle className="spin" size={16} />}{progress}</div>}
      {error && <div className="notice error">{error} <button className="inline-retry" onClick={() => setError('')}><RotateCw size={14} /> Жабу</button></div>}
      {!files.length && <div className="empty-state"><UploadCloud size={32} /><h4>Файл әлі жоқ</h4><p>Телефоннан аудио немесе PDF таңдаңыз. Файл cloud storage-қа сақталады.</p></div>}
      <div className="file-list">
        {files.map((file) => (
          <article className="file-row" key={file.id}>
            <div className="file-icon">{isAudio(file) ? <FileAudio /> : <FileText />}</div>
            <div className="file-meta"><strong>{file.file_name}</strong><small>{formatBytes(file.file_size)} · {new Date(file.created_at).toLocaleDateString('kk-KZ')}</small></div>
            <div className="file-actions">
              {isAudio(file) ? (
                <button className="secondary" disabled={busyFile === file.id} onClick={() => transcribe(file)}>Whisper арқылы мәтінге айналдыру</button>
              ) : (
                <>
                  <button className="secondary" onClick={() => readPdf(file)}><BookOpenText size={16} /> Мәтінін шығару</button>
                  <button className="ghost" onClick={() => openFile(file)}>PDF ашу</button>
                </>
              )}
              <button className="icon-button danger" aria-label="Файлды себетке жіберу" onClick={() => remove(file)}><Trash2 size={17} /></button>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}
